import { Box, Typography } from "@mui/material";
import "./styles.css";
import Latex from "react-latex-next";
import NextAction from "../../utils/NextAction";

export default function Controls({ controls }) {
  const {
    xRotation,
    yRotation,
    zRotation,
    xPosition,
    yPosition,
    zPosition,
    xScale,
    yScale,
    zScale,
    setXRotation,
    setYRotation,
    setZRotation,
    setXPosition,
    setYPosition,
    setZPosition,
    setXScale,
    setYScale,
    setZScale,
  } = controls;
  return (
    <div className="controls">
      <Typography variant="h5" style={{ paddingBottom: 20 }}>
        Transformation
        <br />
        (Translation+Rotation)
      </Typography>
      <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
        In previous lectures, we learn translation and rotation. If we try to
        combine them and <Latex>{`$\\textbf{R}_i^j$`}</Latex> denotes the
        rotation matrix of Frame i with respect to Frame j, we can get
        <Latex>
          {` $$\\textbf{p}^0= \\textbf{o}_1^0 + \\textbf{R}_1^0\\textbf{p}^1$$`}
        </Latex>
        This representation called the <i>coordinate transformation</i>.
      </Typography>
      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        In order to achieve a compact representation of the relationship between
        the coordinates of the same point in two different frames, the
        <i> homogeneous transformation</i> of a generic vector
        <Latex>{`$\\ \\textbf{p}\\ $`}</Latex>
        can be introduced as the vector
        <Latex>{`$\\ \\tilde{\\textbf{ p }}$`}</Latex> formed by adding a fourth
        unit component
        <Latex>
          {` $$\\tilde{\\textbf{ p }} = \\left[ \\begin{array}
          {c} \\textbf{p} \\\\ 1 \\\\ \\end{array}
          \\right] $$`}
        </Latex>
        The coordinate transformation can be written in terms of the (4x4)
        matrix
        <Latex>
          {` $$\\textbf{A}_1^0 = \\left[ \\begin{array}
          {cc} \\textbf{R}_1^0 & \\textbf{o}_1^0 \\\\ \\textbf{0}^T & 1 \\\\ \\end{array}
          \\right] $$`}
        </Latex>
        which is terms <i>homogeneous transformation matrix</i>. Then, the
        coordinate transformation can be compactly rewritten
        <Latex>
          {` $$\\tilde{\\textbf{ p }}^0 = \\textbf{A}_1^0\\tilde{\\textbf{ p }}^1 $$`}
        </Latex>
        We use Roll-Pitch-Yaw(XYZ) Euler Angles here to represent rotation
        matrix
        <Latex>{` $$ \\textbf{R}_1^0 = R_z(\\varphi)R_y(\\vartheta)R_x(\\psi) $$`}</Latex>
        Feel free to change the following value and test it.
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <div className="controlGroup">
            <div className="control">
              <label>X</label>
              <input
                value={xPosition}
                onChange={(e) => setXPosition(e.target.value)}
                step={1 / 32}
                type="number"
              />
            </div>
            <div className="control">
              <label>Y</label>
              <input
                value={yPosition}
                onChange={(e) => setYPosition(e.target.value)}
                step={1 / 32}
                type="number"
              />
            </div>
            <div className="control">
              <label>Z</label>
              <input
                value={zPosition}
                onChange={(e) => setZPosition(e.target.value)}
                step={1 / 32}
                type="number"
              />
            </div>
          </div>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <div className="controlGroup">
            <div className="control">
              <Latex>$\psi\ (degree)$</Latex>
              <input
                value={xRotation}
                onChange={(e) => setXRotation(e.target.value)}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <Latex>$\vartheta\ (degree)$</Latex>
              <input
                value={yRotation}
                onChange={(e) => setYRotation(e.target.value)}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <Latex>$\varphi\ (degree)$</Latex>
              <input
                value={zRotation}
                onChange={(e) => setZRotation(e.target.value)}
                step={1}
                type="number"
              />
            </div>
          </div>
        </Box>
      </Box>
      <NextAction label={"Next: DH parameters"} to="/dh-model" />
    </div>
  );
}
